import {
	CaretRight,
	DotsThreeVertical,
	Plus,
	ShareNetwork,
	SidebarSimple,
	Trash,
	TreeStructure,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useWorkflow } from "../../hooks/useWorkflow.js";
import { ThemeToggle } from "../ThemeToggle.js";
import { StatCard } from "../ui/StatCard.js";
import { StatusPill } from "../ui/StatusPill.js";

interface WorkflowSummary {
	id: string;
	name: string;
	version: number;
	status?: string;
	lastRun?: string;
	runs?: number | string;
}

function IconBtn({
	icon: Icon,
	label,
	onClick,
}: {
	icon: typeof Plus;
	label: string;
	onClick?: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={label}
			className="w-8 h-8 rounded-md hover:bg-accent-bg flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
		>
			<Icon className="w-4 h-4" weight="regular" />
		</button>
	);
}

export function WorkflowList() {
	const { listWorkflows, deleteWorkflow } = useWorkflow();
	const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();

	useEffect(() => {
		listWorkflows()
			.then(setWorkflows)
			.finally(() => setLoading(false));
	}, [listWorkflows]);

	const handleDelete = async (id: string) => {
		const ok = await deleteWorkflow(id);
		if (ok) setWorkflows((wfs) => wfs.filter((w) => w.id !== id));
	};

	const activeCount = workflows.filter((w) => (w.status ?? "draft") === "active").length;
	const totalRuns = workflows.reduce((sum, w) => {
		const n = typeof w.runs === "number" ? w.runs : Number.parseInt(String(w.runs ?? 0), 10);
		return sum + (Number.isFinite(n) ? n : 0);
	}, 0);

	return (
		<div className="h-screen w-full flex flex-col bg-canvas text-text-primary overflow-hidden">
			{/* Top header */}
			<header className="h-14 px-6 flex items-center justify-between border-b border-border-subtle bg-surface/70 backdrop-blur-md shrink-0">
				<div className="flex items-center gap-3">
					<button
						type="button"
						aria-label="Toggle sidebar"
						className="w-8 h-8 rounded-md hover:bg-accent-bg flex items-center justify-center"
					>
						<SidebarSimple className="w-4 h-4 text-text-muted" weight="regular" />
					</button>
					<div className="flex items-center gap-2 text-[14px]">
						<span className="text-text-subtle">NMBLR</span>
						<span className="text-text-subtle/60">/</span>
						<span className="font-medium">Flow</span>
					</div>
				</div>
				<div className="flex items-center gap-1">
					<IconBtn icon={ShareNetwork} label="Share" />
					<IconBtn icon={Plus} label="New" onClick={() => navigate("/workflows/new")} />
					<IconBtn icon={DotsThreeVertical} label="More" />
					<ThemeToggle />
				</div>
			</header>

			{/* Body */}
			<main id="main-content" className="flex-1 overflow-y-auto">
				<div className="px-12 py-12 max-w-[1200px] mx-auto">
					{/* Hero */}
					<div className="flex items-end justify-between mb-10">
						<div>
							<div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full bg-success-bg ring-1 ring-success/20">
								<TreeStructure className="w-3 h-3 text-success-text" weight="bold" />
								<span className="text-[10.5px] font-semibold uppercase tracking-wider text-success-text">
									Flow
								</span>
							</div>
							<h1
								className="text-[42px] font-light tracking-tight text-text-primary leading-[1.05]"
								style={{ letterSpacing: "-0.03em" }}
							>
								Workflows, <span className="text-text-subtle">orchestrated.</span>
							</h1>
							<p className="mt-4 text-[14px] text-text-muted max-w-lg">
								Connect apps, trigger AI steps, and automate the busywork end-to-end.
							</p>
						</div>
						<Link
							to="/workflows/new"
							className="flex items-center gap-2 px-4 py-2.5 bg-accent text-accent-text text-[13px] font-medium rounded-lg hover:bg-accent-hover transition-colors"
						>
							<Plus className="w-4 h-4" weight="bold" />
							New flow
						</Link>
					</div>

					{/* Stats */}
					<div className="grid grid-cols-2 gap-3 mb-8 max-w-xl">
						<StatCard label="Active flows" value={activeCount} />
						<StatCard label="Total runs" value={totalRuns.toLocaleString()} />
					</div>

					{/* List */}
					<div className="bg-surface rounded-2xl border border-border-subtle overflow-hidden">
						<div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between">
							<h3 className="text-[14px] font-medium">Your flows</h3>
							<span className="text-[12px] text-text-muted">
								{loading ? "Loading…" : `${workflows.length} total`}
							</span>
						</div>

						{loading ? (
							<div className="py-12 text-center text-sm text-text-muted">Loading workflows…</div>
						) : workflows.length === 0 ? (
							<div className="py-16 text-center">
								<p className="text-[13.5px] text-text-muted">No workflows yet</p>
								<Link
									to="/workflows/new"
									className="mt-3 inline-block text-[12.5px] font-medium text-text-primary hover:underline"
								>
									Create your first workflow →
								</Link>
							</div>
						) : (
							<div>
								{workflows.map((wf, i) => (
									<div
										key={wf.id}
										tabIndex={0}
										// biome-ignore lint/a11y/useSemanticElements: row contains a nested <button> (delete) and an icon affordance — cannot nest buttons
										role="button"
										onClick={() => navigate(`/workflows/${wf.id}`)}
										onKeyDown={(e) => {
											if (e.key === "Enter") navigate(`/workflows/${wf.id}`);
										}}
										className={`group flex items-center gap-4 px-5 py-4 hover:bg-accent-bg cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-accent ${
											i < workflows.length - 1 ? "border-b border-border-subtle" : ""
										}`}
									>
										<div className="w-9 h-9 rounded-lg bg-accent-bg flex items-center justify-center group-hover:bg-accent/10 transition-colors">
											<TreeStructure className="w-4 h-4 text-text-muted" weight="regular" />
										</div>
										<div className="flex-1 min-w-0">
											<div className="text-[13.5px] font-medium truncate">{wf.name}</div>
											<div className="text-[11.5px] text-text-muted mt-0.5">
												v{wf.version}
												{wf.lastRun ? ` · Last run ${wf.lastRun}` : ""}
											</div>
										</div>
										{wf.runs !== undefined && (
											<div className="text-[12px] text-text-muted tabular-nums">{wf.runs} runs</div>
										)}
										<StatusPill status={wf.status ?? "draft"} />
										<button
											type="button"
											aria-label="Delete workflow"
											onClick={(e) => {
												e.stopPropagation();
												handleDelete(wf.id);
											}}
											className="w-7 h-7 rounded-md text-text-subtle hover:text-error hover:bg-error-bg flex items-center justify-center transition-colors"
										>
											<Trash className="w-3.5 h-3.5" weight="regular" />
										</button>
										<CaretRight
											className="w-4 h-4 text-text-subtle/60 group-hover:text-text-muted transition-colors"
											weight="regular"
										/>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</main>
		</div>
	);
}
